/**
 * Transforms a GoodsItem GraphQL object to/from the detail form state.
 *
 * Like CuDeGoodsShipmentTransformer, toSchema() explicitly lists every
 * field — no spread of original to avoid leaking query-only fields.
 *
 * @see CDS spec: https://quip-amazon.com/haRbADR5yV7P/CuDe-Line-level-details
 */
import { BaseTransformer } from './BaseTransformer';
import { GoodsItemDetailFormState } from '../schemas/forms/goodsItemDetail';
import type { goodsItemFieldsFragment } from '__generated__/graphql';

export type GoodsItemApiData = goodsItemFieldsFragment;

export class GoodsItemDetailTransformer extends BaseTransformer<
  GoodsItemApiData,
  GoodsItemDetailFormState
> {
  toForm(source: GoodsItemApiData): GoodsItemDetailFormState {
    const classifications = source.commodity?.classification ?? [];
    const adjustments = source.valuationAdjustment ?? [];
    const pkg = source.packaging?.[0];
    const imports = source.additionalReference ?? [];
    const govProc = source.governmentProcedure?.[0];

    return {
      // Header
      natureOfTransaction: source.transactionNature ?? null,
      quantity: source.goodsMeasure?.tariff ?? null,
      quantityUnit: null, // TODO: map to actual UoM field once available in the API
      totalValue: source.customsValue?.value ?? null,

      // Classification
      customsDescription: source.commodity?.description ?? null,
      countryOfOrigin: source.origin?.country ?? null,
      classifications: classifications.map((c) => ({
        identification: c.identification ?? null,
        identificationType: c.identificationType ?? null,
        sequence: c.sequence ?? null,
      })),
      grossWeight: source.goodsMeasure?.grossWeight?.value ?? null,
      grossWeightUnit:
        source.goodsMeasure?.grossWeight?.value != null
          ? source.goodsMeasure?.grossWeight?.unit ?? 'KGM'
          : null,
      netWeight: source.goodsMeasure?.netNetWeight?.value ?? null,
      netWeightUnit:
        source.goodsMeasure?.netNetWeight?.value != null
          ? source.goodsMeasure?.netNetWeight?.unit ?? 'KGM'
          : null,

      // Customs Valuation
      valuationMethod: source.customsValuation?.method ?? null,
      valuationAdjustments: adjustments.map((a) => ({
        type: a.addition ?? null,
        amount: a.amount?.value ?? null,
        currency: a.amount?.currency ?? null,
        percentage: a.percentage ?? null,
      })),

      // Packaging
      packagingType: pkg?.type ?? null,
      packagingQuantity: pkg?.quantity ?? null,
      shippingMarks: pkg?.marksNumbers ?? null,

      // Regulations
      importControls: imports.map((r) => ({
        code: r.type ?? null,
        reference: r.identification ?? null,
      })),
      governmentProcedure: govProc?.current ?? null,
      previousProcedure: govProc?.previous ?? null,
      additionalCode: govProc?.additionalProcedure?.[0]?.procedure ?? null,
    };
  }

  toSchema(
    formData: GoodsItemDetailFormState,
    original: GoodsItemApiData,
  ): GoodsItemApiData {
    return {
      sequence: original.sequence,
      transactionNature: formData.natureOfTransaction,
      customsValue:
        formData.totalValue != null
          ? {
              value: formData.totalValue,
              currency: original.customsValue?.currency,
            }
          : original.customsValue,
      goodsMeasure: {
        grossWeight:
          formData.grossWeight != null
            ? { value: formData.grossWeight, unit: formData.grossWeightUnit }
            : null,
        netNetWeight:
          formData.netWeight != null
            ? { value: formData.netWeight, unit: formData.netWeightUnit }
            : null,
        tariff: formData.quantity,
      },
      commodity: {
        description: formData.customsDescription,
        classification: formData.classifications.map((c) => ({
          identification: c.identification,
          identificationType: c.identificationType,
          sequence: c.sequence,
        })),
        dutyTaxFees: original.commodity?.dutyTaxFees,
      },
      origin: {
        country: formData.countryOfOrigin,
        type: original.origin?.type,
      },
      customsValuation: {
        method: formData.valuationMethod,
        freightCharge: original.customsValuation?.freightCharge,
        insurance: original.customsValuation?.insurance,
        chargeDeduction: original.customsValuation?.chargeDeduction,
      },
      valuationAdjustment: formData.valuationAdjustments
        .filter((a) => a.type)
        .map((a) => ({
          addition: a.type,
          amount:
            a.amount != null ? { value: a.amount, currency: a.currency } : null,
          percentage: a.percentage,
        })),
      packaging: [
        {
          type: formData.packagingType,
          quantity: formData.packagingQuantity,
          marksNumbers: formData.shippingMarks,
          ...(original.packaging?.[0]?.sequence != null
            ? { sequence: original.packaging[0].sequence }
            : {}),
        },
      ],
      additionalReference: formData.importControls.map((c) => ({
        type: c.code,
        identification: c.reference,
      })),
      governmentProcedure: [
        {
          current: formData.governmentProcedure,
          previous: formData.previousProcedure,
          description: original.governmentProcedure?.[0]?.description,
          additionalProcedure: formData.additionalCode
            ? [
                {
                  procedure: formData.additionalCode,
                  sequence:
                    original.governmentProcedure?.[0]?.additionalProcedure?.[0]
                      ?.sequence ?? 1,
                },
              ]
            : original.governmentProcedure?.[0]?.additionalProcedure ?? [],
        },
      ],
      seller: original.seller,
    };
  }
}
